import { useState, useCallback } from 'react';
import type { KnowledgeEntry, ConfidenceStats } from '../types/foreman';
import { buildMessageSend } from '../gateway/protocol';

interface UseKnowledgeProps {
  send: (data: unknown) => void;
  activeSessionId: string | null;
}

export function useKnowledge({ send, activeSessionId }: UseKnowledgeProps = { send: () => {}, activeSessionId: null }) {
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [stats, setStats] = useState<ConfidenceStats | null>(null);
  const [searchResults, setSearchResults] = useState<KnowledgeEntry[]>([]);
  const [teachMode, setTeachMode] = useState(false);

  const addEntry = useCallback((entry: KnowledgeEntry) => {
    setEntries((prev) => [entry, ...prev]);
  }, []);

  const searchKnowledge = useCallback((query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const lowerQuery = query.toLowerCase();
    setSearchResults(
      entries.filter(
        (entry) =>
          entry.title.toLowerCase().includes(lowerQuery) ||
          entry.description.toLowerCase().includes(lowerQuery) ||
          entry.source.toLowerCase().includes(lowerQuery) ||
          entry.type.toLowerCase().includes(lowerQuery)
      )
    );
  }, [entries]);

  const toggleTeachMode = useCallback(() => {
    setTeachMode((prev) => !prev);
  }, []);

  const submitCorrection = useCallback(
    (
      messageId: string,
      correction: string,
      category: string
    ) => {
      // Send the correction to the gateway, which routes it to the foreman-learning skill.
      // The message is prefixed with [CORRECTION] so the backend can detect and process it.
      const correctionPayload = `[CORRECTION] messageId=${messageId} category=${category} :: ${correction}`;
      send(buildMessageSend(correctionPayload, activeSessionId || undefined));

      // Also add to local state so the UI reflects the correction immediately
      const entry: KnowledgeEntry = {
        id: Date.now(),
        type: 'correction',
        title: 'Correction submitted',
        description: correction,
        source: 'User correction',
        timestamp: new Date().toISOString(),
        appliedTo: [],
      };
      addEntry(entry);
    },
    [addEntry, send, activeSessionId]
  );

  const refreshStats = useCallback(() => {
    // TODO: Replace mock data with gateway integration.
    // Should send a request like { type: 'knowledge.stats' } to the gateway
    // and update stats from the response via the onMessage handler.
    setStats({
      overall: 87,
      bySkill: {
        'foreman-pricing': { confidence: 82, corrections: 23, trend: 'improving' },
        'foreman-scope': { confidence: 91, corrections: 8, trend: 'stable' },
        'foreman-knowledge': { confidence: 89, corrections: 12, trend: 'stable' },
        'foreman-cart': { confidence: 78, corrections: 5, trend: 'improving' },
      },
      byCategory: {
        kitchens: 88,
        master_baths: 85,
        powder_rooms: 92,
        showers: 79,
      },
      recentCorrections: 4,
      knowledgeEntries: 247,
      patternDbJobs: 50,
      lowConfidenceAreas: [
        'Outdoor kitchens (0 historical jobs)',
        'Large-format tile pricing (3 corrections)',
        'Quartzite vs quartz material costs',
      ],
    });
  }, []);

  return {
    entries,
    stats,
    searchResults,
    teachMode,
    addEntry,
    searchKnowledge,
    toggleTeachMode,
    submitCorrection,
    refreshStats,
  };
}
